import type { IModelInstantiator } from '../../model/interface/instantiator';
import type { IWorkspaceDB } from '../interface/db';
import type { IDBOptions } from '../interface/instantiator';
import type { IDBVersionMigrator } from '../private/migration/interface';
import containerDB from './db';

interface IControllerDB {
    openCount: number;
    db: IWorkspaceDB;
    open: () => Promise<IWorkspaceDB>;
    close: () => void;
    isOpen: () => boolean;
}

async function _open(this: IControllerDB): Promise<IWorkspaceDB> {
    this.openCount += 1;

    if (this.isOpen() && this.openCount > 1) {
        return Promise.resolve(this.db); /* Already open */
    }

    const db = this.db;

    const dbOpen = await db.open();

    return dbOpen;
}

function _close(this: IControllerDB): void {
    this.openCount = Math.max(0, this.openCount - 1);

    if (this.isOpen() && this.openCount <= 0) {
        this.db.close();
    }
}

function _isOpen(this: IControllerDB): boolean {
    return this.db.isOpen();
}

async function _newController(
    modelInstantiator: IModelInstantiator,
    migrators: IDBVersionMigrator[],
    options: IDBOptions,
): Promise<IControllerDB> {
    const db = await containerDB(modelInstantiator, migrators, options);

    const controller: IControllerDB = {
        openCount: 0,
        db,
        open: _open,
        close: _close,
        isOpen: _isOpen,
    };

    return controller;
}

const records: Record<string, IControllerDB> = {};

async function instantiator(
    modelInstantiator: IModelInstantiator,
    migrators: IDBVersionMigrator[],
    options: IDBOptions & { noCache?: boolean },
): Promise<IWorkspaceDB> {
    const recordName = options.name ?? '';
    const existing = records[recordName];

    if (existing && !options.noCache) {
        return existing.db;
    }

    const controller = await _newController(modelInstantiator, migrators, options);
    /* eslint-disable-next-line require-atomic-updates */
    records[recordName] = controller;

    return controller.db;
}

export default instantiator;
